import React, { FC } from 'react';
import { Dialog } from '@mui/material';
import { Clear } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';

import { ICampaign, ICampaignPayload } from 'reduxState/store/campaigns/types';
import { useUpdateCampaignMutation } from 'reduxState/store/campaigns/api';
import { tryCatch } from 'helpers/tryCatch';

interface ICampaignEnableModal {
  isModalOpen: boolean;
  campaign: ICampaign;
  handleCloseModal: () => void;
}

/**
 * CampaignEnableModal component displays a confirmation dialog for enabling a disabled campaign.
 *
 * This modal presents the user with a confirmation message and options to either
 * proceed with enabling the campaign or cancel the operation. When confirmed,
 * it updates the campaign's disabled status to false via the API and shows a success/error toast.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isModalOpen - Controls whether the modal is displayed
 * @param {ICampaign} props.campaign - The campaign to be enabled
 * @param {Function} props.handleCloseModal - Function to close the modal
 *
 * @returns {JSX.Element} A modal dialog with confirmation message and action buttons
 *
 * @example
 * <CampaignEnableModal
 *   isModalOpen={isModalOpen}
 *   campaign={selectedCampaign}
 *   handleCloseModal={handleCloseModal}
 * />
 */

const CampaignEnableModal: FC<ICampaignEnableModal> = ({ isModalOpen, campaign, handleCloseModal }) => {
  const { user } = useAuth0();
  const [updateCampaign] = useUpdateCampaignMutation();
  const { title, id } = campaign;

  const handleEnableCampaign = async (campaign: ICampaign) => {
    const buildCampaignPayload = (campaign: ICampaign) => {
      const {
        config,
        images,
        applications,
        merchantID,
        startDate,
        endDate,
        insertionOrderValue,
        insertionOrderID,
        googleSheetID,
      } = campaign;
      const {
        headline,
        mainText,
        cta,
        html,
        inline,
        htmlPopUp,
        htmlPopUpTemplate,
        alertHTML,
        popUp,
        displayType,
        serpTagline,
        searchPhrases,
        urlPatterns,
        urlPatterns2,
        frequencyCap,
      } = config;

      // Always preserve existing frequency cap settings from the original campaign
      // This ensures user-configured frequency caps are not overwritten
      // Use == null (loose equality) intentionally: catches both null and undefined in one check,
      // while still allowing 0 as a valid value (0 == null is false).
      if (!frequencyCap || !frequencyCap.type || frequencyCap.limit == null || frequencyCap.minutes == null || frequencyCap.cooldown == null) {
        console.error('Campaign missing required frequency cap configuration:', campaign.id);
        throw new Error('Cannot enable campaign: missing frequency cap configuration');
      }

      const updateCampaignPayload: ICampaignPayload = {
        title,
        disabled: false, // Enable the campaign
        merchantID,
        startDate,
        endDate,
        insertionOrderValue,
        insertionOrderID,
        networkMerchantID: null,
        googleSheetID: googleSheetID ?? null,
        config: {
          displayType,
          frequencyCap: {
            type: frequencyCap.type,
            limit: frequencyCap.limit,
            minutes: frequencyCap.minutes,
            cooldown: frequencyCap.cooldown,
          },
          urlPatterns,
          urlPatterns2,
          searchPhrases,
          serpTagline,
        },
        applicationIDs: applications.map(app => app.id),
        imageIDs: images.map(image => image.id),
        modifiedAuthor: user?.email || 'Unknown Wildfire user',
      };

      if (cta) {
        updateCampaignPayload.config.cta = cta;
      }

      if (html) {
        updateCampaignPayload.config.html = html;
      }

      switch (displayType) {
        case 'POP_UP':
          updateCampaignPayload.config.popUp = {
            headline: popUp?.headline || headline || '',
            mainText: popUp?.mainText || mainText || '',
            ctaColor: popUp?.ctaColor || cta?.color || '',
            destinationURL: popUp?.destinationURL || cta?.url || '',
            ctaLabel: popUp?.ctaLabel || cta?.label || '',
          };
          break;
        case 'INLINE':
          updateCampaignPayload.config.inline = {
            target: inline?.target || html?.target || '',
            insertion: inline?.insertion || html?.insertion || 'BEFORE',
            content: inline?.content || htmlPopUp?.content || html?.content || '',
            style: inline?.style || htmlPopUp?.style || html?.style || '',
            destinationURL: inline?.destinationURL || cta?.url || '',
          };
          break;
        case 'HTML_POP_UP':
          updateCampaignPayload.config.htmlPopUp = {
            style: htmlPopUp?.style || html?.style || '',
            content: htmlPopUp?.content || html?.content || '',
            destinationURL: htmlPopUp?.destinationURL || cta?.url || '',
          };
          break;
        case 'HTML_POP_UP_TEMPLATE':
          updateCampaignPayload.config.htmlPopUpTemplate = {
            containerWidth: htmlPopUpTemplate?.containerWidth || '',
            containerBackgroundImageSrc: htmlPopUpTemplate?.containerBackgroundImageSrc || '',
            containerBackgroundImagePosition: htmlPopUpTemplate?.containerBackgroundImagePosition || '',
            containerBackgroundColorOpacity: htmlPopUpTemplate?.containerBackgroundColorOpacity || '',
            topContainerMargin: htmlPopUpTemplate?.topContainerMargin || '',
            merchantLogoWidth: htmlPopUpTemplate?.merchantLogoWidth || '',
            merchantLogoMargin: htmlPopUpTemplate?.merchantLogoMargin || '',
            textColor: htmlPopUpTemplate?.textColor || '',
            textFontFamily: htmlPopUpTemplate?.textFontFamily || '',
            textTitleMargin: htmlPopUpTemplate?.textTitleMargin || '',
            textMargin: htmlPopUpTemplate?.textMargin || '',
            buttonWidth: htmlPopUpTemplate?.buttonWidth || '',
            buttonPadding: htmlPopUpTemplate?.buttonPadding || '',
            buttonImageWidth: htmlPopUpTemplate?.buttonImageWidth || '',
            buttonImageMargin: htmlPopUpTemplate?.buttonImageMargin || '',
            buttonSpanCalloutColor: htmlPopUpTemplate?.buttonSpanCalloutColor || '',
            headerText: htmlPopUpTemplate?.headerText || '',
            subText: htmlPopUpTemplate?.subText || '',
            merchantLogoSrc: htmlPopUpTemplate?.merchantLogoSrc || '',
            merchantLogoAlt: htmlPopUpTemplate?.merchantLogoAlt || '',
            buttonPrefixText: htmlPopUpTemplate?.buttonPrefixText || '',
            buttonTextCallout: htmlPopUpTemplate?.buttonTextCallout || '',
            buttonSuffixText: htmlPopUpTemplate?.buttonSuffixText || '',
            buttonImageSrc: htmlPopUpTemplate?.buttonImageSrc || '',
            buttonImageAlt: htmlPopUpTemplate?.buttonImageAlt || '',
            destinationURL: htmlPopUpTemplate?.destinationURL || cta?.url || '',
          };
          break;
        case 'ALERT_HTML':
          updateCampaignPayload.config.alertHTML = {
            content: alertHTML?.content || '',
            style: alertHTML?.style || '',
            alertTypes: alertHTML?.alertTypes || [],
          };
          if (cta?.url) {
            updateCampaignPayload.config.cta = cta;
          }
          break;
      }
      return updateCampaignPayload;
    };

    let updateCampaignPayload;
    try {
      updateCampaignPayload = buildCampaignPayload(campaign);
    } catch (buildError) {
      toast.error(buildError instanceof Error ? buildError.message : 'Failed to build campaign payload');
      handleCloseModal();
      return;
    }

    const updatePromise = updateCampaign({ id: id.toString(), payload: updateCampaignPayload }).unwrap();
    const { error } = await tryCatch(updatePromise);

    if (error) {
      toast.error('Failed to enable campaign');
      return;
    }

    toast.success('Successfully enabled campaign');
    handleCloseModal();
    return;
  };

  return (
    <Dialog open={isModalOpen} onClose={handleCloseModal} classes={{ paper: 'px-[80px] py-[50px]' }}>
      <div className="absolute top-[15px] right-[15px] cursor-pointer" onClick={handleCloseModal}>
        <Clear />
      </div>
      <p className="mb-[20px]">
        Are you sure you want to enable {title ? `"${title}" campaign` : `Campaign ID: ${id}`}?
      </p>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="bg-white border border-purple text-purple px-4 py-[5px] rounded-md hover:bg-purple hover:text-white"
          onClick={handleCloseModal}
        >
          Cancel
        </button>
        <button
          onClick={() => handleEnableCampaign(campaign)}
          type="button"
          className="text-white bg-green-500 hover:bg-green-600 px-4 py-[5px] rounded-md"
        >
          Enable
        </button>
      </div>
    </Dialog>
  );
};

export default CampaignEnableModal;
